import { useMemo } from 'react';
import { livePassportUrl } from '@/Components/Marketing/links';
import { latestRelease } from '@/Components/Passport/Overview/changelog';
import PassportHeader from '@/Components/Passport/Overview/PassportHeader';
import PassportTabs from '@/Components/Passport/Overview/PassportTabs';
import SummaryRail from '@/Components/Passport/Overview/SummaryRail';
import ValueRow from '@/Components/Passport/Overview/ValueRow';
import ValuesPanel from '@/Components/Passport/Overview/ValuesPanel';
import { groupProperties, isFilled } from '@/Components/Passport/Overview/values';
import { cn } from '@/lib/utils';
import PublishStatusControl from '@/Pages/Passport/Internal/Partials/PublishStatusControl';
import { VignetteTitle } from './VignetteScope';

/*
 * The pack unit's passport in the app, as the "complete" and "release" units
 * play on it (see completeUnit.js, releaseUnit.js): the header with its
 * status pill, the tabs, the values (the two empty ones taking their values
 * from the drawer) and the summary rail following every value. This module
 * is the page as it stands at a point of the story, markup only: it is the
 * hero's first frame, in the page's first load. What moves (the pointer,
 * the camera, the banner's slide, the meter's tween) is the story director's
 * (StoryDirector, a part loaded after the first paint); what the pointer
 * rests on carries `data-story-hover`, which the classes below style as the
 * app styles its hover.
 */

// What stays folded: the groups already complete, so the rows the story
// fills (Performance's rated capacity, and the Safety group) sit in view.
// Folding is the editor's own group toggle.
const FOLDED = ['catIdentity', 'catDesign', 'catSustainability', 'catCondition'];

const noop = () => {};

/** A row the story fills (its "Add value" is what the pointer clicks). */
export const ROW = (id) => `[data-story-row="${id}"] [data-row-base] dd button`;
/** The status pill the pointer clicks to release. */
export const PILL = '[data-story-pill="draft"] button';
/** The page's content, which the story's camera moves. */
export const SCENE = '[data-story-scene]';

// The app's own hover look, on what the pointer rests on: the row's
// `hover:bg-zinc-50/70` and its info glyph (`group-hover:opacity-100`), the
// "Add value" button's `hover:text-zinc-900`, the pill's `hover:bg-zinc-50`.
const HOVER = [
    '[&_[data-story-row][data-story-hover]_.group]:bg-zinc-50/70',
    '[&_[data-story-row][data-story-hover]_dd_button]:text-zinc-900',
    '[&_[data-story-row][data-story-hover]_dt_svg]:opacity-100',
    '[&_[data-story-pill]_button[data-story-hover]]:bg-zinc-50',
].join(' ');

/**
 * What a number input shows of the text typed so far: the browser sets a
 * value that is not yet a number ("75.") to empty, so the dot shows with the
 * digit after it.
 */
const numberShown = (typed) => (typed.endsWith('.') ? typed.slice(0, -1) : typed);

const pages = new WeakMap();

/** The page's values at a stage, worked out once per hero and stage. */
function stageOf(hero, measured, texted, released) {
    if (!pages.has(hero)) pages.set(hero, new Map());
    const stages = pages.get(hero);
    const key = `${measured}|${texted}|${released}`;
    if (!stages.has(key)) {
        const staged = hero.passport({ measured, texted, released });
        const fields = groupProperties(hero.node.properties).flatMap((group) => group.items);
        const setCount = fields.filter(({ property }) => isFilled(staged.data[property.id]?.value)).length;
        stages.set(key, { key, staged, fields, setCount, completeness: fields.length ? Math.round((setCount / fields.length) * 100) : 100 });
    }

    return stages.get(key);
}

/**
 * The passport page's state at story position `at`: which values are saved,
 * whether it is released, the passport as the editor reads it then
 * (`staged`), its completeness, and the drawer (open or not, which editor,
 * what is typed, which language).
 */
export function passportState(hero, at) {
    const { values } = hero;
    const measured = at.reached('complete:save-measure');
    const texted = at.reached('complete:save-text');
    const released = at.reached('release:release');
    const { key, staged, fields, setCount, completeness } = stageOf(hero, measured, texted, released);

    // The drawer holds the editor last opened (so it falls with its content).
    const openText = at.reached('complete:open-text');
    const editor = openText ? 'text' : 'measure';
    const german = at.reached('complete:press-de');
    const typedEn = at.typed('complete:type-en', values.safety.en);
    const typedDe = at.typed('complete:type-de', values.safety.de);
    const open = openText ? !texted : at.reached('complete:open-measure') && !measured;

    return {
        key,
        measured,
        texted,
        released,
        staged,
        fields,
        setCount,
        completeness,
        drawer: {
            open,
            editor,
            language: german ? 'de' : 'en',
            measure: numberShown(at.typed('complete:type-measure', values.capacity)),
            translations: { en: typedEn, de: typedDe },
            // The drawer focuses its input; after the DE tab is pressed, the
            // German one takes focus with the first letter typed into it.
            focus: open && (editor === 'measure' || !german || typedDe.length > 0),
        },
    };
}

/**
 * A row the story fills: the empty row and the filled one stacked in one
 * grid cell, so its value crossfades in place (the label stays put, the
 * height never changes). The old value fades out (150ms); the new one fades
 * in and rises 4px (300ms). Classes reach ValueRow's own `<dd>`: the row is
 * the app's, untouched.
 */
function ResolvingRow({ property, value, resolved, settled, reduced, disabled }) {
    const incoming = <ValueRow property={property} value={value} disabled={disabled} dayFirst />;

    return (
        <div data-story-row={property.id} className="grid">
            {settled ? incoming : (
                <>
                    <div
                        data-row-base=""
                        className={cn(
                            'col-start-1 row-start-1',
                            '[&_dd]:transition-opacity [&_dd]:duration-150 [&_dd]:ease-mk',
                            resolved && '[&_dd]:opacity-0',
                        )}
                    >
                        <ValueRow property={property} value={undefined} disabled={disabled} dayFirst />
                    </div>
                    <div
                        className={cn(
                            'col-start-1 row-start-1 [&_dt]:invisible',
                            '[&_dd]:transition-[opacity,transform] [&_dd]:duration-300 [&_dd]:ease-mk',
                            !resolved && '[&_dd]:opacity-0',
                            !resolved && !reduced && '[&_dd]:translate-y-1',
                        )}
                    >
                        {incoming}
                    </div>
                </>
            )}
        </div>
    );
}

/**
 * The passport page at story position `at`:
 *
 * - `hero`: useHeroData(); `at`: storyAt(); `reduced`: reduced motion.
 * - `parts.drawer`: the ValueDrawer part's module once loaded (the story
 *   holds before the drawer opens until it is); `track`: the scene's
 *   useMotionControls, for the drawer's sheet.
 * - `viewportHeight`: the window's height below its top bar, layout px.
 *
 * The page itself is drawn again only when what it shows changes (a value
 * saved, the release, a row's crossfade settling): the characters typed
 * into the drawer reach the drawer alone.
 */
export default function PassportStoryPage({ hero, at, reduced, track, parts, viewportHeight }) {
    const state = passportState(hero, at);
    const settledMeasure = reduced || at.reached('complete:phone-measure');
    const settledText = reduced || at.reached('complete:phone-text');

    const page = useMemo(
        () => <PassportPage hero={hero} state={state} settledMeasure={settledMeasure} settledText={settledText} reduced={reduced} viewportHeight={viewportHeight} />,
        // `state.key` names the stage; the rest of `state` follows from it.
        [hero, state.key, settledMeasure, settledText, reduced, viewportHeight],
    );

    const Drawer = parts?.drawer?.default;

    return (
        <div className={cn('absolute inset-0', HOVER)}>
            {page}
            {Drawer && !reduced && (
                <Drawer
                    {...state.drawer}
                    property={state.drawer.editor === 'text' ? hero.text : hero.measure}
                    reduced={reduced}
                    track={track}
                />
            )}
        </div>
    );
}

/** The page's markup at a stage (see PassportStoryPage). */
function PassportPage({ hero, state, settledMeasure, settledText, reduced, viewportHeight }) {
    const { kestrel, measure, text } = hero;
    const { measured, texted, released, staged, fields, setCount } = state;

    const final = useMemo(() => hero.passport(), [hero]);
    const draft = useMemo(() => hero.passport({ released: false }), [hero]);

    const panelState = useMemo(() => ({
        query: '',
        setQuery: noop,
        missingOnly: false,
        setMissingOnly: noop,
        collapsedGroups: new Set(FOLDED.map((key) => kestrel.properties[key])),
        toggleGroup: noop,
        open: true,
        setOpen: noop,
        showMissing: noop,
    }), [kestrel]);

    const renderValue = (property) => {
        const filling = property.id === measure.id ? measured : property.id === text.id ? texted : null;
        if (filling === null) {
            return <ValueRow property={property} value={staged.data[property.id]?.value} disabled={released} dayFirst />;
        }

        return (
            <ResolvingRow
                property={property}
                value={final.data[property.id]?.value}
                resolved={filling}
                // Once its crossfade is over, the row is one ValueRow again.
                settled={property.id === measure.id ? settledMeasure : settledText}
                reduced={reduced}
                disabled={released}
            />
        );
    };

    const pill = (passport, shown, name) => (
        <span
            data-story-pill={name}
            className={cn('col-start-1 row-start-1 transition-opacity duration-200 ease-mk', shown ? 'opacity-100' : 'opacity-0')}
        >
            <PublishStatusControl passportData={passport} ontologyId={hero.ontology.ontology_id} />
        </span>
    );

    return (
        <div data-story-scene="" className="relative bg-zinc-50/60" style={{ minHeight: viewportHeight }}>
            <div className="bg-white px-6 pt-5">
                <div data-story-header="">
                    <PassportHeader
                        linked={false}
                        dayFirst
                        titleAs={VignetteTitle}
                        passport={staged}
                        node={hero.node}
                        ontology={hero.ontology}
                        timeZone={hero.clock.timeZone}
                        publicUrl={livePassportUrl()}
                        statusControl={(
                            <span className="grid justify-items-end">
                                {pill(draft, !released, 'draft')}
                                {pill(final, released, 'released')}
                            </span>
                        )}
                        reviseBlockedReason={null}
                    />
                </div>
                <div data-flip="">
                    <PassportTabs
                        value="overview"
                        onChange={noop}
                        showImport
                        changelogCount={staged.changelog.length}
                        showDataRequests
                    />
                </div>
            </div>

            <div data-flip="" className="p-6">
                <div className="grid grid-cols-[minmax(0,1fr)_264px] items-start gap-6">
                    <ValuesPanel
                        dayFirst
                        properties={hero.node.properties}
                        values={staged.data}
                        friendlyId={staged.friendly_id}
                        readOnly={released}
                        state={panelState}
                        renderValue={renderValue}
                        defaultLanguage={hero.ontology.default_language}
                        idPrefix="vignette-hero-values"
                    />
                    <div
                        data-vignette-rail=""
                        className={cn(
                            '[&_[role=progressbar]>div]:origin-left',
                            '[&_[role=progressbar]>div]:transition-colors [&_[role=progressbar]>div]:duration-300 [&_[role=progressbar]>div]:ease-mk',
                        )}
                    >
                        <SummaryRail
                            dayFirst
                            setCount={setCount}
                            fieldCount={fields.length}
                            status={staged.publish_status}
                            lastRelease={latestRelease(staged.changelog)}
                            releaseNumber={staged.release_number}
                            schemaVersion={hero.ontology.version}
                            latestVersion={hero.ontology.latest_version}
                            createdAt={staged.meta.created_at}
                            timeZone={hero.clock.timeZone}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
